import { DocumentNode } from "@apollo/client";
import { DrawMode } from "@mapbox/mapbox-gl-draw";
import { useState } from "react";

import useMapEditorFeatureCreate from "../api/useMapEditorFeatureCreate";
import useMapEditorFeatureUpdate from "../api/useMapEditorFeatureUpdate";
import { FeatureCreateOption } from "../components/MapEditorFeatureCreateDialog/components/types";

type UseMapEditorDrawStateParams = {
    selectedLayerId?: string;
    selectedFeature?: GeoJSON.Feature;
    projectId: string;
    visitId?: string;
    onSelect: (layerId: string, featureId: string) => void;
    featureMutateRefetchQueries?: DocumentNode[];
};

export function useMapEditorDrawState({
    selectedLayerId,
    selectedFeature,
    projectId,
    visitId,
    onSelect,
    featureMutateRefetchQueries = [],
}: UseMapEditorDrawStateParams) {
    const [drawMode, setDrawMode] = useState<DrawMode>("simple_select");
    const [featureCreateOption, setFeatureCreateOption] = useState<FeatureCreateOption>(FeatureCreateOption.none());

    const { featureCreate } = useMapEditorFeatureCreate(featureMutateRefetchQueries, visitId);
    const { featureUpdate } = useMapEditorFeatureUpdate(featureMutateRefetchQueries);

    const onDeselect = () => {
        setDrawMode("simple_select");
    };

    const onFeatureCreate = async (feature: GeoJSON.Feature) => {
        if (!selectedLayerId) return;

        const createdFeatureId = await featureCreate({
            geometry: feature.geometry,
            projectId,
            mapEditorLayerId: selectedLayerId,
            options: featureCreateOption,
        });

        if (!createdFeatureId) return;

        onSelect(selectedLayerId, createdFeatureId);
    };

    const onFeatureUpdate = (feature: GeoJSON.Feature) => {
        if (!selectedFeature || !feature.properties?.id) return;

        featureUpdate({
            featureId: feature.properties.id,
            geometry: feature.geometry,
        });
    };

    return {
        drawMode,
        setDrawMode,
        featureCreateOption,
        setFeatureCreateOption,
        onDeselect,
        onFeatureCreate,
        onFeatureUpdate,
    };
}
